/**
 * Скопировать в буфер строку, которая станет известна только ПОСЛЕ запроса.
 *
 * Браузеры пускают в буфер только внутри жеста пользователя. Safari рвёт
 * «активацию» на первом же `await`, поэтому там строку отдаём обещанием через
 * `ClipboardItem` — его можно создать синхронно в обработчике клика, а
 * заполнить потом. Chrome и Firefox разрешают `writeText` и после ответа
 * сервера, пока вкладка в фокусе.
 *
 * 🔴 Строка — ссылка на подпись, bearer-секрет: её не логируем, не рисуем и
 * не возвращаем наружу. Вызывающему достаётся только «получилось или нет».
 */

type ClipboardItemCtor = new (items: Record<string, Promise<Blob>>) => unknown;

interface IAsyncClipboard {
  write?: (items: unknown[]) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
}

/** Последний рубеж: выделение во временном поле и `execCommand('copy')`. */
const legacyCopy = (text: string): boolean => {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(area);
  return ok;
};

export type CopyOutcome = 'copied' | 'empty' | 'failed';

export const copyProducedText = async (
  produce: () => Promise<string | null>,
): Promise<CopyOutcome> => {
  const clipboard = (navigator.clipboard as unknown) as
    | IAsyncClipboard
    | undefined;
  const Item = ((window as unknown) as { ClipboardItem?: ClipboardItemCtor })
    .ClipboardItem;

  // Ответ сервера нужен и для буфера, и для итога: держим его в замыкании.
  let produced: Promise<string | null> | null = null;
  const run = () => {
    if (!produced) produced = produce();
    return produced;
  };

  if (Item && clipboard?.write) {
    try {
      const blob = run().then((text) => {
        if (!text) throw new Error('empty');
        return new Blob([text], { type: 'text/plain' });
      });
      await clipboard.write([new Item({ 'text/plain': blob })]);
      return 'copied';
    } catch {
      // Ниже — второй способ. Если текста нет вовсе, он ответит «empty».
    }
  }

  let text: string | null;
  try {
    text = await run();
  } catch {
    return 'failed';
  }
  if (!text) return 'empty';

  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(text);
      return 'copied';
    } catch {
      // Нет разрешения или вкладка без фокуса — пробуем по-старому.
    }
  }
  return legacyCopy(text) ? 'copied' : 'failed';
};
